import { filterClasses } from "@swivl-mono/helpers";
import {
  TicketPriority,
  TicketStatus,
  orderedTicketPriorities,
  orderedTicketStatuses,
  ticketPriorityLabels,
  ticketStatusLabels
} from "@swivl-mono/types";
import { DropdownContainer, DropdownOption, DropdownOptionItem, DropdownOptionVariant, DropdownPosition } from "@swivl-mono/ui";
import React, { useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import { IoFilter } from "react-icons/io5";
import { RiProgress4Line } from "react-icons/ri";

import { useTicketingStore } from "../../../Models/Ticketing/Ticketing.Store";

// Status filter — one multi-select checkbox row per status.
const STATUS_FILTER_OPTIONS: DropdownOptionItem[] = orderedTicketStatuses.map((status) => ({
  label: ticketStatusLabels[status],
  value: status,
  variant: DropdownOptionVariant.WITH_CHECKBOX
}));

// Two-level Order menu. "Order" sorts the list (leaf values "sort:<by>:<dir>");
// "Priority" filters by priority value (leaf values "prio:all" clears, else
// "prio:<priority>"). Parent rows are submenu triggers only.
const ORDER_SORT_OPTIONS: DropdownOptionItem[] = [
  {
    label: "Order",
    value: "order",
    options: [
      { label: "Last Updated: Newest", value: "sort:updatedAt:desc" },
      { label: "Last Updated: Oldest", value: "sort:updatedAt:asc" },
      { label: "Date Created: Newest", value: "sort:createdAt:desc" },
      { label: "Date Created: Oldest", value: "sort:createdAt:asc" }
    ]
  },
  {
    label: "Priority",
    value: "priority",
    options: [{ label: "All", value: "prio:all" }, ...orderedTicketPriorities.map((p) => ({ label: ticketPriorityLabels[p], value: `prio:${p}` }))]
  }
];

const CHIP_CLASS =
  "group flex cursor-pointer items-center gap-1 rounded bg-grey-50 px-2 py-1 text-sm font-semibold transition outline-none hover:bg-grey-100 data-[state=open]:bg-grey-100 group-data-[state=open]:bg-grey-100";
const CHEVRON_CLASS = "h-4 w-4 text-grey-600 transition group-data-[state=open]:rotate-180";

/**
 * The Status (filter) and Order (sort) controls above the ticket list, per the
 * Group Inbox design. Both read/write the shared inbox query so they stay in
 * sync with the persisted filters, and both use the shared nested-dropdown
 * component (DropdownContainer + DropdownOption).
 */
export function TicketListControls(): React.JSX.Element {
  const query = useTicketingStore((s) => s.inboxQuery);
  const setQuery = useTicketingStore((s) => s.setInboxQuery);
  const refresh = useTicketingStore((s) => s.refreshInbox);

  const selectedStatuses = query.statuses ?? [];
  const sortBy = query.sortBy ?? "createdAt";
  const sortDir = query.sortDir ?? "desc";
  const priorityFilter = query.priority;

  const [statusOpen, setStatusOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);

  // Checkbox options toggle in place (the menu stays open) — add/remove the
  // status and clear the filter entirely when the last one is unchecked.
  const toggleStatus = (value: string): void => {
    const status = value as TicketStatus;
    const next = selectedStatuses.includes(status) ? selectedStatuses.filter((s) => s !== status) : [...selectedStatuses, status];
    setQuery({ statuses: next.length > 0 ? next : undefined, offset: 0 });
    void refresh();
  };

  const isStatusSelected = (value: string): boolean => selectedStatuses.includes(value as TicketStatus);

  // "sort:<by>:<dir>" changes the sort; "prio:all|normal|high" sets the priority
  // filter ("all" clears it). Parent rows are submenu triggers and never arrive.
  const applyOrderSelection = (value: string): void => {
    if (value.startsWith("sort:")) {
      const [, nextSortBy, nextSortDir] = value.split(":");
      if (nextSortBy !== "createdAt" && nextSortBy !== "updatedAt") return;
      if (nextSortDir !== "asc" && nextSortDir !== "desc") return;
      setQuery({ sortBy: nextSortBy, sortDir: nextSortDir, offset: 0 });
    } else if (value.startsWith("prio:")) {
      const key = value.slice("prio:".length);
      const priority = orderedTicketPriorities.includes(key as TicketPriority) ? (key as TicketPriority) : undefined;
      setQuery({ priority, offset: 0 });
    } else {
      return;
    }
    void refresh();
    setOrderOpen(false);
  };

  const isOrderOptionSelected = (value: string): boolean => {
    if (value.startsWith("sort:")) {
      const [, sb, sd] = value.split(":");
      return sb === sortBy && sd === sortDir;
    }
    if (value === "prio:all") return priorityFilter == null;
    if (value.startsWith("prio:")) return priorityFilter === (value.slice("prio:".length) as TicketPriority);
    return false;
  };

  return (
    <div className="flex items-start justify-between px-2">
      {/* Status filter — multi-select checkbox rows via the shared component. */}
      <DropdownContainer
        isOpen={statusOpen}
        onOpenChange={setStatusOpen}
        position={DropdownPosition.LEFT}
        modal={false}
        classNames={{ wrapper: "group cursor-pointer" }}
        trigger={
          <button className={filterClasses(CHIP_CLASS, selectedStatuses.length > 0 ? "text-orange-500" : "text-black")} type="button">
            <RiProgress4Line className="h-4 w-4" />
            <span>Status{selectedStatuses.length > 0 ? ` (${selectedStatuses.length})` : ""}</span>
            <FiChevronDown className={CHEVRON_CLASS} />
          </button>
        }
      >
        {STATUS_FILTER_OPTIONS.map((option) => (
          <DropdownOption key={option.value} option={option} onSelect={toggleStatus} isOptionSelected={isStatusSelected} />
        ))}
      </DropdownContainer>

      {/* Order (sort) — first level Order / Priority, each opening a submenu of
          concrete sort choices, via the shared nested-dropdown component. */}
      <DropdownContainer
        isOpen={orderOpen}
        onOpenChange={setOrderOpen}
        position={DropdownPosition.LEFT}
        modal={false}
        // First level is only submenu triggers, which the shared component
        // doesn't floor at min-w-40 the way leaf rows are — set it here so the
        // panel matches the design system's 160px minimum.
        classNames={{ wrapper: "group cursor-pointer", dropdown: "min-w-40" }}
        trigger={
          <button className={filterClasses(CHIP_CLASS, "text-black")} type="button">
            <IoFilter className="h-4 w-4" />
            <span>Order</span>
            <FiChevronDown className={CHEVRON_CLASS} />
          </button>
        }
      >
        {ORDER_SORT_OPTIONS.map((option) => (
          <DropdownOption key={option.value} option={option} onSelect={applyOrderSelection} isOptionSelected={isOrderOptionSelected} />
        ))}
      </DropdownContainer>
    </div>
  );
}
